import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useParams,
  useSearchParams,
} from "react-router";
import { AppInitializer } from "./components/AppInitializer";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { GroupProvider } from "./contexts/GroupContext";
import { ItemsCacheProvider } from "./contexts/ItemsCacheContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { useScreenTracking } from "./hooks/useScreenTracking";
import { isLoggedIn } from "./lib/auth";
import { isInLiff } from "./lib/liff";

// 静的import（コアページ）
import { Home } from "./pages/Home";
import { ItemNew } from "./pages/ItemNew";
import { Settings } from "./pages/Settings";

// 遅延読み込み
const Contact = lazy(() => import("./pages/Contact"));
const Paywall = lazy(() => import("./pages/Paywall"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Login = lazy(() => import("./pages/Login"));
const Callback = lazy(() => import("./pages/Callback"));
const EmailLogin = lazy(() => import("./pages/EmailLogin"));
const GroupCreate = lazy(() => import("./pages/GroupCreate"));
const GroupJoin = lazy(() => import("./pages/GroupJoin"));
const GroupSettings = lazy(() => import("./pages/GroupSettings"));
const Admin = lazy(() => import("./pages/Admin"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));

function AuthGuard({ children }: { children: React.ReactNode }) {
  if (!isLoggedIn()) {
    const currentPath = window.location.pathname + window.location.search;
    const to =
      currentPath === "/login"
        ? "/login"
        : `/login?redirect=${encodeURIComponent(currentPath)}`;
    return <Navigate to={to} replace />;
  }
  return <>{children}</>;
}

function OnboardingGuard({ children }: { children: React.ReactNode }) {
  // LIFF内ではオンボーディングをスキップ
  if (isInLiff()) {
    localStorage.setItem("kaidoki_onboarding_done", "1");
    return <>{children}</>;
  }
  const done = localStorage.getItem("kaidoki_onboarding_done");
  if (!done) {
    const searchParams = new URLSearchParams(window.location.search);
    const redirect = searchParams.get("redirect");
    const to = redirect ? `/?redirect=${encodeURIComponent(redirect)}` : "/";
    return <Navigate to={to} replace />;
  }
  return <>{children}</>;
}

/** /items/:id → /home?item=:id に転送（通知ディープリンク対応） */
function ItemDetailRedirect() {
  const params = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const action = searchParams.get("action");
  const to = action
    ? `/home?item=${params.id}&action=${action}`
    : `/home?item=${params.id}`;
  return <Navigate to={to} replace />;
}

function ScreenTracker() {
  useScreenTracking();
  return null;
}

function PageLoading() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-dvh items-center justify-center bg-white">
      <div className="text-center">
        <div className="mb-4 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
        </div>
        <p className="text-sm text-gray-500">{t("common:loading")}</p>
      </div>
    </div>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScreenTracker />
        <SubscriptionProvider>
          <GroupProvider>
            <ItemsCacheProvider>
              <AppInitializer>
                <Suspense fallback={<PageLoading />}>
                  <Routes>
                    <Route
                      path="/"
                      element={
                        isInLiff() ? (
                          isLoggedIn() ? (
                            <Navigate to="/home" replace />
                          ) : (
                            <Navigate to="/login" replace />
                          )
                        ) : (
                          <Onboarding />
                        )
                      }
                    />
                    <Route
                      path="/login"
                      element={
                        <OnboardingGuard>
                          <Login />
                        </OnboardingGuard>
                      }
                    />
                    <Route
                      path="/login/email"
                      element={
                        <OnboardingGuard>
                          <EmailLogin />
                        </OnboardingGuard>
                      }
                    />
                    <Route path="/auth/line/callback" element={<Callback />} />
                    <Route
                      path="/home"
                      element={
                        <AuthGuard>
                          <OnboardingGuard>
                            <Home />
                          </OnboardingGuard>
                        </AuthGuard>
                      }
                    />
                    <Route
                      path="/items/new"
                      element={
                        <AuthGuard>
                          <OnboardingGuard>
                            <ItemNew />
                          </OnboardingGuard>
                        </AuthGuard>
                      }
                    />
                    <Route
                      path="/items/:id"
                      element={
                        <AuthGuard>
                          <OnboardingGuard>
                            <ItemDetailRedirect />
                          </OnboardingGuard>
                        </AuthGuard>
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                        <AuthGuard>
                          <OnboardingGuard>
                            <Settings />
                          </OnboardingGuard>
                        </AuthGuard>
                      }
                    />
                    <Route
                      path="/groups/new"
                      element={
                        <AuthGuard>
                          <OnboardingGuard>
                            <GroupCreate />
                          </OnboardingGuard>
                        </AuthGuard>
                      }
                    />
                    <Route
                      path="/groups/join"
                      element={
                        <AuthGuard>
                          <OnboardingGuard>
                            <GroupJoin />
                          </OnboardingGuard>
                        </AuthGuard>
                      }
                    />
                    <Route
                      path="/groups/:id"
                      element={
                        <AuthGuard>
                          <OnboardingGuard>
                            <GroupSettings />
                          </OnboardingGuard>
                        </AuthGuard>
                      }
                    />
                    <Route
                      path="/contact"
                      element={
                        <AuthGuard>
                          <OnboardingGuard>
                            <Contact />
                          </OnboardingGuard>
                        </AuthGuard>
                      }
                    />
                    <Route
                      path="/paywall"
                      element={
                        <AuthGuard>
                          <OnboardingGuard>
                            <Paywall />
                          </OnboardingGuard>
                        </AuthGuard>
                      }
                    />
                    <Route
                      path="/admin"
                      element={
                        <AuthGuard>
                          <Admin />
                        </AuthGuard>
                      }
                    />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
              </AppInitializer>
            </ItemsCacheProvider>
          </GroupProvider>
        </SubscriptionProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
